import React from "react";
import { TextInput } from "@narmi/design_system";
import TooltipTextInput from "../TooltipTextInput";
import { ReactLocalization } from "@fluent/react";

/**
 *
 * NDS TextInput configured for entering Tax ID (SSN, EIN).
 * `TaxIDTextInput` is intended to be used as a controlled component.
 * Only numeric characters are permitted,
 * and the value displayed will be formatted as the user types as XXX - XX - XXXX (or XX - XXXXXXX if the `ein` prop is `true`).
 * However, the value transmitted up to the parent via `onChange` will only include numeric characters as XXXXXXXXX
 *
 */

export const formatEin = (value: string) => {
  if (!value) return "";
  if (value.length < 2) return value;
  return `${value.substring(0, 2)}-${value.substring(2, 9)}`;
};

export const formatSsnOrTaxID = (value: string) => {
  if (!value) return "";
  if (value.length < 3) return value;
  if (value.length < 5) return `${value.substring(0, 3)}-${value.substring(3, 5)}`;
  return `${value.substring(0, 3)}-${value.substring(3, 5)}-${value.substring(5, 9)}`;
};

export const validateTaxID = (value: string) => {
  if (value && value.replace(/-/g, "").length < 9) {
    return "Please enter a valid Tax ID/EIN";
  }
  return null;
};

export const validateSsn = (ssn: string, l10n?: ReactLocalization) => {
  /*
  Procedure for identifying invalid SSNs: https://secure.ssa.gov/apps10/poms.nsf/lnx/0110201035

  To understand the regex, visit: https://regex101.com/r/IwOMIj/2
  */

  if (ssn.length != 9) {
    if (l10n) {
      return l10n.getString("error-tax-id-incomplete", null, "Must contain 9 digits");
    }
    return "Must contain 9 digits";
  }

  const regex = /^(666\d{6}|000\d{6}|\d{3}00\d{4}|\d{5}0000)$/;
  if (regex.test(ssn)) {
    if (l10n) {
      return l10n.getString("error-invalid-tax-id", null, "Please enter a valid SSN");
    }
    return "Please enter a valid SSN";
  }

  // ITINs always start with 9
  if (ssn.startsWith("9")) {
    const middleItinDigits = ssn.slice(3, 5);
    // https://www.irs.gov/pub/irs-pdf/p4757.pdf
    // ITIN is valid if 4th and 5th digits range from 50 to 65, 70 to 88,
    // 90 to 92, and 94 to 99
    // https://regex101.com/r/ttzrHz/1
    const itinRegex = /^(5\d{1}|6[0-5]|7\d{1}|8[0-8]|9[0-2]|9[4-9])$/;
    if (!itinRegex.test(middleItinDigits)) {
      if (l10n) {
        return l10n.getString("error-invalid-itin", null, "Please enter a valid tax ID");
      }
      return "Please enter a valid tax ID";
    }
  }
  return null;
};

function stripToTaxId(value: string) {
  return value.replace(/[^\d]/g, "").substring(0, 9);
}

interface MaskedTaxIDTextInputProps {
  isInitiallyMasked: boolean;
  label: string;
  tooltipText?: string;
  onChange: (value: string) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
  error?: string;
}
export const MaskedTaxIDTextInput = React.forwardRef(
  (
    {
      isInitiallyMasked,
      label,
      tooltipText,
      onChange,
      onBlur,
      value,
      error,
    }: MaskedTaxIDTextInputProps,
    parentRef
  ) => {
    const [showMask, setShowMask] = React.useState(isInitiallyMasked);

    if (showMask) {
      return (
        <TextInput value="***-**-****" label={label} readOnly onFocus={() => setShowMask(false)} />
      );
    }

    return (
      <TaxIDTextInput
        ref={parentRef}
        label={label}
        tooltipText={tooltipText}
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        error={error}
        autoComplete="off"
      />
    );
  }
);

interface TaxIDTextInputProps {
  ein?: boolean;
  value?: string;
  onChange: (value: string) => void;
  label: string;
  name?: string;
  id?: string;
  tooltipText?: string;
}
const TaxIDTextInput = React.forwardRef(
  (
    {
      ein = false,
      value = "",
      onChange,
      label,
      name = "tax_id",
      id = "tax_id",
      tooltipText = "",
      ...otherTextInputProps
    }: TaxIDTextInputProps | React.ComponentProps<typeof TextInput>,
    forwardedRef
  ) => {
    const ref = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(forwardedRef, () => ref.current);

    const Component: React.ComponentType<React.ComponentProps<typeof TextInput>> = tooltipText
      ? TooltipTextInput
      : TextInput;
    const conditionalProps: Partial<React.ComponentProps<typeof TextInput>> = {};

    if (tooltipText) {
      conditionalProps.tooltipText = tooltipText;
    }

    const renderedValue = ein ? formatEin(value) : formatSsnOrTaxID(value);

    const [cursorPosition, setCursorPosition] = React.useState<number>();

    React.useEffect(() => {
      if (!ref.current || cursorPosition === undefined) {
        return;
      }
      const input = ref.current;
      // if the value at the cursor is a dash, go to the next character
      const index =
        renderedValue.charAt(cursorPosition) === "-" ? cursorPosition + 1 : cursorPosition;
      requestAnimationFrame(() => {
        input.setSelectionRange(index, index);
      });
    }, [ref.current, cursorPosition, renderedValue]);

    return (
      <Component
        id={id}
        name={name}
        label={label}
        ref={ref}
        value={renderedValue}
        inputMode="numeric"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const index = (event.target as HTMLInputElement)?.selectionStart;
          if (index === null) {
            // this might be through automation or something, so directly set the value.
            onChange(stripToTaxId(event.target.value));
            return;
          }
          const isBackspace =
            (event.nativeEvent as InputEvent).inputType === "deleteContentBackward";
          if (isBackspace && renderedValue.charAt(index) === "-") {
            // the character deleted was a dash, so we need to find the dash and delete the character before it
            setCursorPosition(index - 1);
            onChange(
              stripToTaxId(renderedValue.substring(0, index - 1) + renderedValue.substring(index))
            );
            return;
          }
          const isPaste = (event.nativeEvent as InputEvent).inputType === "insertFromPaste";
          if (isPaste) {
            // if this is from a paste, don't manipulate the cursor position.
            setCursorPosition(undefined);
          } else {
            setCursorPosition(index);
          }
          onChange(stripToTaxId(event.target.value));
        }}
        {...conditionalProps}
        {...otherTextInputProps}
      />
    );
  }
);

export default TaxIDTextInput;
